import React, {
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  GripVertical,
  MapPin,
  Plus,
  Repeat2,
  Send,
  UserRound,
} from "lucide-react";

const SERVICE_STYLE = {
  "Personal Care": "border-rose-200 bg-rose-50 text-rose-950",
  "Community Access": "border-sky-200 bg-sky-50 text-sky-950",
  "Domestic Assistance": "border-amber-200 bg-amber-50 text-amber-950",
  Transport: "border-indigo-200 bg-indigo-50 text-indigo-950",
  "Social Support": "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-950",
  "Meal Preparation": "border-orange-200 bg-orange-50 text-orange-950",
  "Medication Support": "border-teal-200 bg-teal-50 text-teal-950",
  Respite: "border-violet-200 bg-violet-50 text-violet-950",
  "Supported Independent Living":
    "border-emerald-200 bg-emerald-50 text-emerald-950",
  Other: "border-slate-200 bg-slate-50 text-slate-950",
};

const STATUS_DOT = {
  draft: "bg-violet-500",
  upcoming: "bg-amber-500",
  in_progress: "bg-blue-500",
  completed: "bg-emerald-500",
  cancelled: "bg-red-500",
};

const PROTECTED_STATUSES = new Set([
  "completed",
  "in_progress",
  "clocked_on",
  "clocked_off",
]);

function pad(value) {
  return String(value).padStart(2, "0");
}

function localDateString(date) {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-");
}

function minutesFromTime(value) {
  if (!value || !String(value).includes(":")) {
    return null;
  }

  const [hours, minutes] = String(value)
    .slice(0, 5)
    .split(":")
    .map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

function durationMinutes(startTime, endTime) {
  const start = minutesFromTime(startTime);
  const end = minutesFromTime(endTime);

  if (start === null || end === null) {
    return 0;
  }

  if (end > start) {
    return end - start;
  }

  if (end < start) {
    return (24 * 60) - start + end;
  }

  return 0;
}

function formatDuration(minutes) {
  if (!minutes) {
    return "0h";
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (!remainder) {
    return `${hours}h`;
  }

  if (!hours) {
    return `${remainder}m`;
  }

  return `${hours}h ${remainder}m`;
}

function getPersonName(person, fallback = "Unknown worker") {
  if (!person) {
    return fallback;
  }

  return (
    person.full_name
    || person.name
    || [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ")
    || fallback
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function publicationLabel(visit) {
  return (
    visit.publication_status === "draft"
    || visit.status === "draft"
  )
    ? "Draft"
    : "Published";
}

function ShiftCellCard({
  visit,
  participantName,
  conflict,
  onOpen,
  onDragStart,
  onDragEnd,
  draggingVisitId,
  selectedVisitId,
  onSelectMove,
}) {
  const serviceClass =
    SERVICE_STYLE[visit.service_type]
    || SERVICE_STYLE.Other;

  const minutes = durationMinutes(
    visit.start_time,
    visit.end_time,
  );

  const isDraft =
    visit.publication_status === "draft"
    || visit.status === "draft";

  const draggable = !PROTECTED_STATUSES.has(
    visit.status
  );

  return (
    <article
      className={[
        "group relative w-full rounded-xl border p-2.5 text-left shadow-sm",
        "transition hover:-translate-y-0.5 hover:shadow-lg",
        draggable ? "cursor-default" : "cursor-not-allowed",
        serviceClass,
        conflict ? "ring-2 ring-red-300" : "",
        visit.status === "cancelled" ? "opacity-55" : "",
        draggingVisitId === visit.id
          ? "scale-95 opacity-45"
          : "",
        selectedVisitId === visit.id
          ? "ring-4 ring-emerald-400"
          : "",
      ].join(" ")}
      title={[
        `${visit.start_time || ""}–${visit.end_time || ""}`,
        participantName,
        visit.service_type,
        visit.address,
        draggable
          ? "Drag, or click the grip then choose a destination cell"
          : "Active or completed shifts cannot be moved",
      ].filter(Boolean).join("\n")}
    >
      <button
        type="button"
        onClick={() => onOpen(visit)}
        className="block w-full text-left"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-1.5">
            <span
              draggable={draggable}
              onDragStart={(event) =>
                onDragStart(event, visit)
              }
              onDragEnd={onDragEnd}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (draggable) {
                  onSelectMove(visit);
                }
              }}
              className={[
                "mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center",
                "rounded-md border transition",
                draggable
                  ? "cursor-grab border-slate-300 bg-white text-slate-500 hover:border-emerald-400 hover:text-emerald-700 active:cursor-grabbing"
                  : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-300",
              ].join(" ")}
              title={
                draggable
                  ? "Drag shift"
                  : "Active or completed shifts cannot be moved"
              }
              aria-label="Drag shift"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </span>

            <div className="min-w-0">
              <p className="text-[11px] font-black">
                {String(visit.start_time || "").slice(0, 5)}
                {"–"}
                {String(visit.end_time || "").slice(0, 5)}
              </p>

              <p className="mt-1 line-clamp-2 text-xs font-black">
                {participantName}
              </p>
            </div>
          </div>

          <span
            className={[
              "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
              STATUS_DOT[visit.status] || STATUS_DOT.upcoming,
            ].join(" ")}
          />
        </div>

        <div className="mt-2 flex flex-wrap gap-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-950 px-2 py-0.5 text-[9px] font-bold text-white">
            <Clock3 className="h-2.5 w-2.5" />
            {formatDuration(minutes)}
          </span>

          <span
            className={[
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
              "text-[9px] font-bold",
              isDraft
                ? "bg-violet-100 text-violet-800"
                : "bg-emerald-100 text-emerald-800",
            ].join(" ")}
          >
            {isDraft ? (
              <Clock3 className="h-2.5 w-2.5" />
            ) : (
              <Send className="h-2.5 w-2.5" />
            )}
            {publicationLabel(visit)}
          </span>

          {visit.recurring_series_id && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[9px] font-bold text-indigo-800">
              <Repeat2 className="h-2.5 w-2.5" />
              Recurring
            </span>
          )}
        </div>

        {visit.service_type && (
          <p className="mt-2 truncate text-[10px] font-semibold opacity-75">
            {visit.service_type}
          </p>
        )}

        {visit.address && (
          <p className="mt-1 flex items-start gap-1 text-[9px] leading-3 opacity-70">
            <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" />
            <span className="line-clamp-2">
              {visit.address}
            </span>
          </p>
        )}

        {conflict && (
          <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-black text-red-700">
            <AlertTriangle className="h-3 w-3" />
            Worker overlap
          </p>
        )}
      </button>

      {draggingVisitId === visit.id && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-slate-950/65 text-xs font-black text-white">
          Moving shift…
        </div>
      )}

      {selectedVisitId === visit.id && !draggingVisitId && (
        <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded-lg bg-emerald-600 px-2 py-1 text-center text-[9px] font-black text-white shadow">
          Selected — choose destination
        </div>
      )}
    </article>
  );
}

export default function StaffPlannerView({
  staff,
  weekDays,
  weekVisits,
  participantLookup,
  conflictsByVisitId,
  staffFilter,
  onOpenVisit,
  onCreateVisit,
  onMoveVisit,
  onDragStart,
  onDragEnd,
  draggingVisitId,
}) {
  const [sortMode, setSortMode] = useState(
    "alphabetical"
  );
  const [dragTarget, setDragTarget] = useState(null);
  const [selectedVisitId, setSelectedVisitId] = useState(null);

  const visitsByStaffAndDate = useMemo(() => {
    const result = {};

    weekVisits.forEach((visit) => {
      const staffId = visit.staff_id || "__unassigned__";
      const key = `${staffId}:${visit.date}`;

      if (!result[key]) {
        result[key] = [];
      }

      result[key].push(visit);
    });

    Object.values(result).forEach((items) => {
      items.sort((first, second) =>
        String(first.start_time || "").localeCompare(
          String(second.start_time || "")
        )
      );
    });

    return result;
  }, [weekVisits]);

  const staffMetrics = useMemo(() => {
    const result = {};

    weekVisits.forEach((visit) => {
      const staffId = visit.staff_id || "__unassigned__";

      if (!result[staffId]) {
        result[staffId] = {
          minutes: 0,
          visits: 0,
          drafts: 0,
          published: 0,
          conflicts: 0,
        };
      }

      const metric = result[staffId];
      metric.visits += 1;

      if (visit.status !== "cancelled") {
        metric.minutes += durationMinutes(
          visit.start_time,
          visit.end_time,
        );
      }

      if (
        visit.publication_status === "draft"
        || visit.status === "draft"
      ) {
        metric.drafts += 1;
      } else {
        metric.published += 1;
      }

      if (conflictsByVisitId.has(visit.id)) {
        metric.conflicts += 1;
      }
    });

    return result;
  }, [conflictsByVisitId, weekVisits]);

  const visibleStaff = useMemo(() => {
    const filtered = staffFilter
      ? staff.filter((worker) => worker.id === staffFilter)
      : staff;

    return [...filtered].sort((first, second) => {
      const firstName = getPersonName(first);
      const secondName = getPersonName(second);
      const firstMinutes =
        staffMetrics[first.id]?.minutes || 0;
      const secondMinutes =
        staffMetrics[second.id]?.minutes || 0;

      if (sortMode === "hours_desc") {
        return (
          secondMinutes - firstMinutes
          || firstName.localeCompare(secondName, "en-AU")
        );
      }

      if (sortMode === "hours_asc") {
        return (
          firstMinutes - secondMinutes
          || firstName.localeCompare(secondName, "en-AU")
        );
      }

      return firstName.localeCompare(
        secondName,
        "en-AU",
        { sensitivity: "base" },
      );
    });
  }, [sortMode, staff, staffFilter, staffMetrics]);

  const unassignedVisits = weekVisits.filter(
    (visit) => !visit.staff_id
  );

  const rows = [
    ...visibleStaff.map((worker) => ({
      id: worker.id,
      worker,
      name: getPersonName(worker),
      unassigned: false,
    })),
    ...(unassignedVisits.length
      ? [{
          id: "__unassigned__",
          worker: null,
          name: "Unassigned visits",
          unassigned: true,
        }]
      : []),
  ];

  const handleSelectMove = (visit) => {
    if (!visit || PROTECTED_STATUSES.has(visit.status)) {
      return;
    }

    setSelectedVisitId((current) =>
      current === visit.id ? null : visit.id
    );
  };

  const handleCellClick = async (
    row,
    date
  ) => {
    if (
      !selectedVisitId
      || row.unassigned
    ) {
      return;
    }

    const visit = weekVisits.find(
      (item) => item.id === selectedVisitId
    );

    if (!visit) {
      setSelectedVisitId(null);
      return;
    }

    await onMoveVisit(
      visit,
      date,
      row.id
    );

    setSelectedVisitId(null);
  };

  const handleCellDragOver = (
    event,
    staffId,
    date
  ) => {
    if (staffId === "__unassigned__") {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragTarget(`${staffId}:${date}`);
  };

  const handleCellDragLeave = (
    event,
    key
  ) => {
    if (
      event.currentTarget.contains(
        event.relatedTarget
      )
    ) {
      return;
    }

    setDragTarget((current) =>
      current === key ? null : current
    );
  };

  const handleCellDrop = async (
    event,
    staffId,
    date
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setDragTarget(null);

    if (staffId === "__unassigned__") {
      return;
    }

    const transferredVisitId =
      event.dataTransfer.getData(
        "application/x-cruzzn-visit-id"
      )
      || event.dataTransfer.getData("text/plain")
      || draggingVisitId;

    const visit = weekVisits.find(
      (item) => item.id === transferredVisitId
    );

    try {
      event.dataTransfer.clearData();
    } catch (clearError) {
      // Browser may lock the drag store after reading.
    }

    if (!visit) {
      return;
    }

    await onMoveVisit(
      visit,
      date,
      staffId
    );
  };

  if (!rows.length) {
    return (
      <section className="rounded-2xl border bg-white p-10 text-center shadow-sm">
        <UserRound className="mx-auto h-9 w-9 text-slate-400" />
        <h2 className="mt-3 font-bold text-slate-900">
          No active staff available
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Activate staff records or clear the current worker filter.
        </p>
      </section>
    );
  }

  return (
    <section
      className="overflow-hidden rounded-2xl border bg-white shadow-sm"
      data-testid="atlas-staff-view-v40c3"
    >
      <div className="border-b bg-slate-950 px-4 py-3 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-black">
              Staff Timeline
            </h2>
            <p className="text-xs text-slate-300">
              Drag a movable shift onto another worker and day.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {(draggingVisitId || selectedVisitId) && (
              <div className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-black text-white shadow-lg">
                Choose a destination staff/day cell
              </div>
            )}

            {selectedVisitId && !draggingVisitId && (
              <button
                type="button"
                onClick={() => setSelectedVisitId(null)}
                className="rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-xs font-bold text-white hover:bg-white/20"
              >
                Cancel move
              </button>
            )}

            <label className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold">
              Sort
              <select
                value={sortMode}
                onChange={(event) =>
                  setSortMode(event.target.value)
                }
                className="rounded-md border border-white/20 bg-slate-900 px-2 py-1 text-xs text-white outline-none"
              >
                <option value="alphabetical">
                  Alphabetical
                </option>
                <option value="hours_desc">
                  Most hours
                </option>
                <option value="hours_asc">
                  Least hours
                </option>
              </select>
            </label>

            <div className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold">
              <CalendarDays className="h-4 w-4" />
              {rows.length} row{rows.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>

      <div className="max-h-[72vh] overflow-auto">
        <div className="grid min-w-[1780px] grid-cols-[270px_repeat(7,minmax(215px,1fr))]">
          <div className="sticky left-0 top-0 z-40 border-b border-r bg-slate-100 px-4 py-3 shadow-[4px_0_10px_-8px_rgba(15,23,42,0.7)]">
            <p className="text-xs font-black uppercase tracking-wide text-slate-600">
              Staff member
            </p>
            <p className="mt-1 text-[10px] text-slate-500">
              Hours, visits and publish readiness
            </p>
          </div>

          {weekDays.map((day) => {
            const date = localDateString(day);
            const today =
              date === localDateString(new Date());

            const dayVisits = weekVisits.filter(
              (visit) => visit.date === date
            );

            const dayMinutes = dayVisits
              .filter((visit) => visit.status !== "cancelled")
              .reduce(
                (total, visit) =>
                  total
                  + durationMinutes(
                    visit.start_time,
                    visit.end_time,
                  ),
                0,
              );

            return (
              <div
                key={date}
                className={[
                  "sticky top-0 z-30 border-b border-r px-3 py-3",
                  today ? "bg-emerald-100" : "bg-slate-50",
                ].join(" ")}
              >
                <p
                  className={[
                    "font-black",
                    today
                      ? "text-emerald-900"
                      : "text-slate-900",
                  ].join(" ")}
                >
                  {day.toLocaleDateString("en-AU", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                </p>

                <p className="mt-1 text-[10px] text-slate-500">
                  {dayVisits.length} shift
                  {dayVisits.length === 1 ? "" : "s"}
                  {" · "}
                  {formatDuration(dayMinutes)}
                </p>
              </div>
            );
          })}

          {rows.map((row, rowIndex) => {
            const metric = staffMetrics[row.id] || {
              minutes: 0,
              visits: 0,
              drafts: 0,
              published: 0,
              conflicts: 0,
            };

            const utilisation = Math.min(
              100,
              Math.round(
                (metric.minutes / (38 * 60)) * 100
              ),
            );

            const above38 = metric.minutes > 38 * 60;

            return (
              <React.Fragment key={row.id}>
                <div
                  className={[
                    "sticky left-0 z-20 min-h-[170px] border-b border-r p-3",
                    "shadow-[4px_0_10px_-8px_rgba(15,23,42,0.7)]",
                    row.unassigned
                      ? "bg-amber-50"
                      : rowIndex % 2
                        ? "bg-slate-50"
                        : "bg-white",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={[
                        "flex h-10 w-10 shrink-0 items-center justify-center",
                        "rounded-full text-xs font-black text-white",
                        row.unassigned
                          ? "bg-amber-600"
                          : "bg-slate-900",
                      ].join(" ")}
                    >
                      {row.unassigned
                        ? "!"
                        : initials(row.name)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-black text-slate-950">
                        {row.name}
                      </p>

                      {row.worker?.email && (
                        <p className="mt-0.5 truncate text-[10px] text-slate-500">
                          {row.worker.email}
                        </p>
                      )}

                      <div className="mt-2 grid grid-cols-2 gap-1.5">
                        <div className="rounded-lg bg-slate-950 px-2 py-1.5 text-white">
                          <p className="text-[8px] font-bold uppercase tracking-wide text-slate-300">
                            Hours
                          </p>
                          <p className="text-xs font-black">
                            {formatDuration(metric.minutes)}
                          </p>
                        </div>

                        <div className="rounded-lg bg-slate-100 px-2 py-1.5 text-slate-900">
                          <p className="text-[8px] font-bold uppercase tracking-wide text-slate-500">
                            Visits
                          </p>
                          <p className="text-xs font-black">
                            {metric.visits}
                          </p>
                        </div>
                      </div>

                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[9px] font-bold text-slate-500">
                          <span>38h capacity</span>
                          <span>{utilisation}%</span>
                        </div>

                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className={[
                              "h-full rounded-full transition-all",
                              above38
                                ? "bg-red-500"
                                : utilisation >= 90
                                  ? "bg-amber-500"
                                  : "bg-emerald-500",
                            ].join(" ")}
                            style={{
                              width: `${Math.max(
                                metric.minutes ? 3 : 0,
                                utilisation,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-1">
                        {metric.published > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[8px] font-bold text-emerald-800">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            {metric.published} published
                          </span>
                        )}

                        {metric.drafts > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[8px] font-bold text-violet-800">
                            <Clock3 className="h-2.5 w-2.5" />
                            {metric.drafts} draft
                          </span>
                        )}

                        {metric.conflicts > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[8px] font-bold text-red-800">
                            <AlertTriangle className="h-2.5 w-2.5" />
                            {metric.conflicts} conflict
                          </span>
                        )}
                      </div>

                      {above38 && (
                        <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-black text-red-700">
                          <AlertTriangle className="h-3 w-3" />
                          Above 38 hours
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {weekDays.map((day) => {
                  const date = localDateString(day);
                  const key = `${row.id}:${date}`;
                  const dayVisits =
                    visitsByStaffAndDate[key] || [];

                  const dayMinutes = dayVisits
                    .filter(
                      (visit) =>
                        visit.status !== "cancelled"
                    )
                    .reduce(
                      (total, visit) =>
                        total
                        + durationMinutes(
                          visit.start_time,
                          visit.end_time,
                        ),
                      0,
                    );

                  const isTarget = dragTarget === key;

                  return (
                    <div
                      key={key}
                      role={selectedVisitId && !row.unassigned ? "button" : undefined}
                      tabIndex={selectedVisitId && !row.unassigned ? 0 : undefined}
                      onClick={() =>
                        handleCellClick(row, date)
                      }
                      onKeyDown={(event) => {
                        if (
                          selectedVisitId
                          && !row.unassigned
                          && (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          handleCellClick(row, date);
                        }
                      }}
                      onDragOver={(event) =>
                        handleCellDragOver(
                          event,
                          row.id,
                          date,
                        )
                      }
                      onDragLeave={(event) =>
                        handleCellDragLeave(
                          event,
                          key,
                        )
                      }
                      onDrop={(event) =>
                        handleCellDrop(
                          event,
                          row.id,
                          date,
                        )
                      }
                      className={[
                        "group relative min-h-[170px] border-b border-r p-2 transition",
                        rowIndex % 2
                          ? "bg-slate-50/40"
                          : "bg-white",
                        isTarget
                          ? "bg-emerald-100 ring-4 ring-inset ring-emerald-400"
                          : "",
                        selectedVisitId && !row.unassigned
                          ? "cursor-pointer hover:bg-emerald-50 hover:ring-2 hover:ring-inset hover:ring-emerald-300"
                          : "",
                      ].join(" ")}
                    >
                      {selectedVisitId && !row.unassigned && !isTarget && (
                        <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/70 text-center text-xs font-black text-emerald-800 opacity-0 transition group-hover:opacity-100">
                          Click to move here
                        </div>
                      )}

                      {isTarget && (
                        <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-emerald-600 bg-emerald-50/90 text-center text-xs font-black text-emerald-900">
                          Drop shift here
                        </div>
                      )}

                      {dayVisits.length > 0 && (
                        <div className="mb-2 flex items-center justify-between rounded-md bg-slate-100 px-2 py-1 text-[9px] font-bold text-slate-500">
                          <span>
                            {dayVisits.length} visit
                            {dayVisits.length === 1 ? "" : "s"}
                          </span>
                          <span>
                            {formatDuration(dayMinutes)}
                          </span>
                        </div>
                      )}

                      <div className="space-y-2">
                        {dayVisits.map((visit) => {
                          const participantName =
                            visit.client_name
                            || participantLookup[visit.client_id]
                            || "Participant";

                          return (
                            <ShiftCellCard
                              key={`${visit.id}-${visit.start_time}-${visit.end_time}`}
                              visit={visit}
                              participantName={participantName}
                              conflict={conflictsByVisitId.has(
                                visit.id
                              )}
                              onOpen={onOpenVisit}
                              onDragStart={onDragStart}
                              onDragEnd={onDragEnd}
                              draggingVisitId={draggingVisitId}
                              selectedVisitId={selectedVisitId}
                              onSelectMove={handleSelectMove}
                            />
                          );
                        })}

                        {!row.unassigned && (
                          <button
                            type="button"
                            onClick={() =>
                              onCreateVisit(date, row.id)
                            }
                            className={[
                              "flex w-full items-center justify-center gap-1",
                              "rounded-lg border border-dashed border-slate-300",
                              "px-2 py-2 text-[10px] font-bold text-slate-400",
                              "hover:border-emerald-400 hover:bg-emerald-50",
                              "hover:text-emerald-700",
                            ].join(" ")}
                          >
                            <Plus className="h-3 w-3" />
                            Add shift
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
}
